import { useState } from 'react'
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  IconButton,
  Input,
  InputGroup,
  Menu,
  Portal,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiChevronDown, FiChevronRight, FiCopy, FiMoreVertical, FiRefreshCw, FiTrash2 } from 'react-icons/fi'
import { useRotateCode, useRotateLink, useRevokeGrant } from '../../../hooks/useCampaign'
import { useFormatInstanceCode } from '../../../hooks/useInstanceCode'
import { extractApiMessage } from '../../../lib/apiError'
import { publicOrigin } from '../../../lib/native/instance'
import { ResponsiveTable, ResponsiveModal, StatusChip, PersonAvatar, type ResponsiveColumn, type StatusVariant } from '../../shared'
import { CanvasserSessionRows } from './CanvasserSessionRows'
import type { CanvasserGrantRow, CanvasserGrantStatus } from '../../../types/canvasserGrant'

// L11 slice 1e, Task 16: organizer-facing canvasser roster. Mirrors
// components/lobbyDay/recruit/RosterView.tsx's shape -- a ResponsiveTable with a single
// hand-rolled per-row Menu.Root (FiMoreVertical trigger) covering ALL of rotate code / rotate
// link / remove, rather than splitting "remove" out into ResponsiveTable's own `rowActions`
// (the brief wants one overflow menu with three items, not two surfaces). The rotate mutations'
// results are shown-once credentials (same show-once contract as MintCanvasserModal), so each
// rotate opens its own small ResponsiveModal instead of just a toast -- a toast would let the
// code/link scroll away before it's copied.

interface CanvasserRosterTableProps {
  campaignId: string
  rows: CanvasserGrantRow[]
}

// Status idiom (spec: info for Active, warning for Expired, danger for Revoked/Blocked).
// StatusChip owns the actual bg/fg token pairs -- this table only picks WHICH idiom applies.
const STATUS_VARIANT: Record<CanvasserGrantStatus, StatusVariant> = {
  Active: 'info',
  Expired: 'warning',
  Revoked: 'danger',
  Blocked: 'danger',
}

export function CanvasserRosterTable({ campaignId, rows }: CanvasserRosterTableProps) {
  const rotateCode = useRotateCode(campaignId)
  const rotateLink = useRotateLink(campaignId)
  const revokeGrant = useRevokeGrant(campaignId)
  const formatCode = useFormatInstanceCode() // [Native B1, NB1-11] <orgId>-<CODE>

  const [rotatingCodeId, setRotatingCodeId] = useState<string | null>(null)
  const [rotatingLinkId, setRotatingLinkId] = useState<string | null>(null)
  const [newCode, setNewCode] = useState<{ fullName: string; shortCode: string; shortCodeExpiresAt: string } | null>(null)
  const [newLink, setNewLink] = useState<{ fullName: string; magicLink: string } | null>(null)
  const [removeTarget, setRemoveTarget] = useState<CanvasserGrantRow | null>(null)
  const [reasonNote, setReasonNote] = useState('')
  const [removeError, setRemoveError] = useState<string | null>(null)
  // Task 17: which row's device-session panel is expanded, if any -- at most one at a time.
  const [expandedGrantId, setExpandedGrantId] = useState<string | null>(null)

  const handleRotateCode = async (row: CanvasserGrantRow) => {
    setRotatingCodeId(row.grantId)
    try {
      const result = await rotateCode.mutateAsync(row.grantId)
      setNewCode({ fullName: row.fullName, shortCode: result.shortCode, shortCodeExpiresAt: result.shortCodeExpiresAt })
    } catch (err) {
      toaster.create({ type: 'error', title: extractApiMessage(err, 'Failed to rotate code.') })
    } finally {
      setRotatingCodeId(null)
    }
  }

  const handleRotateLink = async (row: CanvasserGrantRow) => {
    setRotatingLinkId(row.grantId)
    try {
      const result = await rotateLink.mutateAsync(row.grantId)
      // [Native B1] The instance's origin; null on native with no instance → no link, copy disabled.
      const origin = publicOrigin()
      setNewLink({ fullName: row.fullName, magicLink: origin ? `${origin}${result.magicLinkPath}` : '' })
    } catch (err) {
      toaster.create({ type: 'error', title: extractApiMessage(err, 'Failed to rotate link.') })
    } finally {
      setRotatingLinkId(null)
    }
  }

  const openRemove = (row: CanvasserGrantRow) => {
    setRemoveTarget(row)
    setReasonNote('')
    setRemoveError(null)
  }

  const closeRemove = () => {
    setRemoveTarget(null)
    setReasonNote('')
    setRemoveError(null)
  }

  const confirmRemove = async () => {
    if (!removeTarget) return
    setRemoveError(null)
    try {
      await revokeGrant.mutateAsync({
        grantId: removeTarget.grantId,
        body: { reasonCode: 'OrganizerRevoked', reasonNote: reasonNote.trim() || undefined },
      })
      toaster.create({ type: 'success', title: `${removeTarget.fullName} removed` })
      closeRemove()
    } catch (err) {
      setRemoveError(extractApiMessage(err, 'Failed to remove canvasser.'))
    }
  }

  const copyCode = () => {
    if (!newCode) return
    navigator.clipboard.writeText(formatCode(newCode.shortCode))
    toaster.create({ type: 'success', title: 'Code copied' })
  }

  const copyLink = () => {
    if (!newLink?.magicLink) return
    navigator.clipboard.writeText(newLink.magicLink)
    toaster.create({ type: 'success', title: 'Link copied' })
  }

  const toggleExpanded = (row: CanvasserGrantRow) => {
    setExpandedGrantId((cur) => (cur === row.grantId ? null : row.grantId))
  }

  const columns: ResponsiveColumn<CanvasserGrantRow>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (r) => (
        <HStack gap={1}>
          <IconButton
            aria-label={expandedGrantId === r.grantId ? `Collapse devices for ${r.fullName}` : `Show devices for ${r.fullName}`}
            variant="ghost"
            size="sm"
            minW="44px"
            minH="44px"
            _touch={{ minW: '44px', minH: '44px' }}
            onClick={(e) => { e.stopPropagation(); toggleExpanded(r) }}
          >
            {expandedGrantId === r.grantId ? <FiChevronDown /> : <FiChevronRight />}
          </IconButton>
          <PersonAvatar name={r.fullName} src={r.portraitUrl} size="sm" />
          <Text fontWeight="medium">{r.fullName}</Text>
          {r.isSelfEnrolled && (
            <StatusChip variant="info">Self</StatusChip>
          )}
          {r.isSelfEnrolled && r.assignedTurfCount === 0 && (
            <StatusChip variant="warning">Needs turf</StatusChip>
          )}
        </HStack>
      ),
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (r) => <StatusChip variant={STATUS_VARIANT[r.status]}>{r.status}</StatusChip>,
    },
    {
      header: 'Turfs',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (r) => r.assignedTurfCount,
    },
    {
      header: 'Devices',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (r) => r.activeSessionCount,
    },
    {
      header: 'Actions',
      mobileRole: 'meta',
      cell: (r) => (
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <IconButton
              aria-label="Canvasser actions"
              variant="ghost"
              size="sm"
              minW="44px"
              minH="44px"
              _touch={{ minW: '44px', minH: '44px' }}
              loading={rotatingCodeId === r.grantId || rotatingLinkId === r.grantId}
              onClick={(e) => e.stopPropagation()}
            >
              <FiMoreVertical />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content onClick={(e) => e.stopPropagation()}>
                <Menu.Item
                  value="rotate-code"
                  disabled={rotatingCodeId === r.grantId}
                  onSelect={() => handleRotateCode(r)}
                >
                  <HStack gap={2}>
                    {rotatingCodeId === r.grantId ? <Spinner size="xs" /> : <FiRefreshCw />}
                    <Text>Rotate code</Text>
                  </HStack>
                </Menu.Item>
                <Menu.Item
                  value="rotate-link"
                  disabled={rotatingLinkId === r.grantId}
                  onSelect={() => handleRotateLink(r)}
                >
                  <HStack gap={2}>
                    {rotatingLinkId === r.grantId ? <Spinner size="xs" /> : <FiRefreshCw />}
                    <Text>Rotate link</Text>
                  </HStack>
                </Menu.Item>
                <Menu.Item value="remove" color="danger.fg" onSelect={() => openRemove(r)}>
                  <HStack gap={2}>
                    <FiTrash2 />
                    <Text>Remove canvasser</Text>
                  </HStack>
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      ),
    },
  ]

  return (
    <Box>
      <ResponsiveTable
        columns={columns}
        rows={rows}
        keyFn={(r) => r.grantId}
        emptyMessage="No canvassers yet."
        bare
        expandedRowKey={expandedGrantId}
        renderExpanded={(r) => <CanvasserSessionRows campaignId={campaignId} grantId={r.grantId} />}
      />

      <ResponsiveModal isOpen={!!newCode} onClose={() => setNewCode(null)} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>New code for {newCode?.fullName}</Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} />
            </Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" color="muted">
                  This code is shown only once -- copy it now. The old code can no longer be used to
                  sign in. Anyone already signed in on their phone stays signed in -- use Remove to
                  end their access.
                </Text>
                <Field.Root>
                  <Field.Label>Short code</Field.Label>
                  <InputGroup endElement={
                    <IconButton aria-label="Copy code" size="sm" minH="44px" variant="ghost" onClick={copyCode}>
                      <FiCopy />
                    </IconButton>
                  }>
                    <Input size="sm" value={newCode ? formatCode(newCode.shortCode) : ''} readOnly />
                  </InputGroup>
                </Field.Root>
                {!!newCode && (
                  <Text fontSize="sm" color="muted">
                    Expires {new Date(newCode.shortCodeExpiresAt).toLocaleString()}
                  </Text>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button colorPalette="brand" size="sm" minH="44px" onClick={() => setNewCode(null)}>Done</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>

      <ResponsiveModal isOpen={!!newLink} onClose={() => setNewLink(null)} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>New link for {newLink?.fullName}</Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} />
            </Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" color="muted">
                  This link is shown only once -- copy it now. The old link can no longer be used to
                  sign in. Anyone already signed in on their phone stays signed in -- use Remove to
                  end their access.
                </Text>
                <Field.Root>
                  <Field.Label>Magic link</Field.Label>
                  <InputGroup endElement={
                    <IconButton aria-label="Copy link" size="sm" minH="44px" variant="ghost" disabled={!newLink?.magicLink} onClick={copyLink}>
                      <FiCopy />
                    </IconButton>
                  }>
                    <Input size="sm" value={newLink?.magicLink ?? ''} readOnly />
                  </InputGroup>
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button colorPalette="brand" size="sm" minH="44px" onClick={() => setNewLink(null)}>Done</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>

      <ResponsiveModal isOpen={!!removeTarget} onClose={closeRemove} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Remove {removeTarget?.fullName}?</Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} />
            </Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={3} align="stretch">
                {!!removeError && <Text color="danger.fg" fontSize="sm">{removeError}</Text>}
                <Text fontSize="sm" color="muted">
                  This revokes their access immediately -- they will not be able to sign back in
                  with their current code or link.
                </Text>
                <Field.Root>
                  <Field.Label>Reason (optional)</Field.Label>
                  <Textarea
                    size="sm"
                    value={reasonNote}
                    onChange={(e) => setReasonNote(e.target.value)}
                  />
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="outline" size="sm" minH="44px" onClick={closeRemove}>Cancel</Button>
                <Button
                  variant="outline"
                  borderColor="danger.fg"
                  color="danger.fg"
                  size="sm"
                  minH="44px"
                  onClick={confirmRemove}
                  loading={revokeGrant.isPending}
                >
                  Remove canvasser
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </Box>
  );
}
